import { type FormEvent, useState } from 'react';
import { api, ApiError, call, type Schemas } from '../../api/client';
import { useAction, usePriorities } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Button, ErrorText, Field, Input, Modal, Select, Textarea } from '../../components/ui';
import { scheduleSummary, WEEKDAYS } from '../../lib/format';
import { readSchedule, ScheduleFields } from './SeriesPages';

type Task = Schemas['TaskResponse'];
type Repeat = 'NEVER' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'CUSTOM';

const today = () => new Date().toLocaleDateString('en-CA');

/** "Repeat: Weekly" from a start date: on that weekday / day of the month / date every year (Custom shows everything). */
export function presetSchedule(repeat: Exclude<Repeat, 'NEVER' | 'CUSTOM'>, startsOn: string): Schemas['ScheduleDto'] {
  const [, month, day] = startsOn.split('-').map(Number) as [number, number, number];
  const weekday = WEEKDAYS[(new Date(`${startsOn}T12:00:00`).getDay() + 6) % 7]!;
  const base = { interval: 1, timing: 'FIXED_DAY' as const, startsOn };
  switch (repeat) {
    case 'DAILY': return { ...base, frequency: 'DAILY' };
    case 'WEEKLY': return { ...base, frequency: 'WEEKLY', weekdays: [weekday] };
    case 'MONTHLY': return { ...base, frequency: 'MONTHLY', monthDay: day };
    case 'YEARLY': return { ...base, frequency: 'YEARLY', monthOfYear: month, monthDay: day };
  }
}

const summaryOf = (s: Schemas['ScheduleDto']) =>
  scheduleSummary({ weekdays: [], monthDay: null, monthOfYear: null, endsOn: null, ...s, interval: s.interval ?? 1 } as Schemas['ScheduleResponse']);

const fieldError = (err: unknown, field: string) => (err instanceof ApiError ? err.problem.errors?.find((e) => e.field === field)?.message : undefined);

/** Create a one-off task, or edit an existing one (with optimistic version check). */
export function TaskFormDialog({ open, onOpenChange, task, defaultClaim = false }: { open: boolean; onOpenChange(o: boolean): void; task?: Task; defaultClaim?: boolean }) {
  const { familyId } = useHousehold();
  const priorities = usePriorities().data ?? [];
  const [claim, setClaim] = useState(defaultClaim);
  const [repeat, setRepeat] = useState<Repeat>('NEVER');
  const [dueOn, setDueOn] = useState(task?.dueOn ?? '');
  // New tasks, and existing one-off tasks the server says may repeat (not subtasks, child chores or partners' tasks).
  const canRepeat = !task || task.allowedActions.includes('REPEAT');
  const save = useAction(
    async (f: FormData) => {
      const content = {
        title: String(f.get('title')),
        description: (f.get('description') as string) || null,
        priority: String(f.get('priority')),
        estimatedMinutes: f.get('estimatedMinutes') ? Number(f.get('estimatedMinutes')) : null,
        // A subtask's points are its share of the main task's (ADR-0022), so they're never sent for one.
        ...(task?.parent ? {} : { points: f.get('points') ? Number(f.get('points')) : null }),
      };
      const dueOn = (f.get('dueOn') as string) || undefined;
      if (!task && repeat !== 'NEVER') {
        // A repeating task is a recurring series (docs/recurring-tasks.md); its first occurrence appears right away.
        const schedule = repeat === 'CUSTOM' ? readSchedule(f) : presetSchedule(repeat, dueOn ?? today());
        return call(
          api.POST('/api/v1/families/{familyId}/series', {
            params: { path: { familyId }, header: { 'Idempotency-Key': crypto.randomUUID() } },
            body: { ...content, description: content.description ?? undefined, schedule, standing: claim ? 'ME' : 'POOL' },
          }),
        );
      }
      if (task) {
        const updated = await call(
          api.PATCH('/api/v1/families/{familyId}/tasks/{taskId}', {
            params: { path: { familyId, taskId: task.id } },
            body: { expectedVersion: task.version, ...content, dueOn: dueOn ?? null },
          }),
        );
        if (repeat === 'NEVER') return updated;
        // Make it repeat: the task becomes the first occurrence of a new recurring series.
        const schedule = repeat === 'CUSTOM' ? readSchedule(f) : presetSchedule(repeat, dueOn ?? today());
        return call(
          api.POST('/api/v1/families/{familyId}/tasks/{taskId}/repeat', {
            params: { path: { familyId, taskId: task.id } },
            body: { expectedVersion: updated.version, schedule },
          }),
        );
      }
      return call(
        api.POST('/api/v1/families/{familyId}/tasks', {
          params: { path: { familyId }, header: { 'Idempotency-Key': crypto.randomUUID() } },
          body: { ...content, description: content.description ?? undefined, dueOn, claim },
        }),
      );
    },
    { success: repeat !== 'NEVER' ? (task ? 'Task now repeats' : 'Repeating task added') : task ? 'Task updated' : 'Task added' },
  );

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    save.mutate(new FormData(e.currentTarget), { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={task ? 'Edit task' : 'New task'}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="What needs doing?" error={fieldError(save.error, 'title')}>
          {(id) => <Input id={id} name="title" required maxLength={200} defaultValue={task?.title} autoFocus />}
        </Field>
        <Field label="Details (optional)">{(id) => <Textarea id={id} name="description" maxLength={4000} defaultValue={task?.description ?? ''} />}</Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            {(id) => (
              // Remount once options load so the default selection applies.
              <Select id={id} name="priority" key={priorities.length} defaultValue={task?.priority.code ?? priorities.find((p) => p.isDefault)?.code ?? 'NORMAL'}>
                {priorities.map((p) => (
                  <option key={p.code} value={p.code}>{p.label}</option>
                ))}
              </Select>
            )}
          </Field>
          {repeat !== 'CUSTOM' ? (
            <Field label={repeat === 'NEVER' ? 'Due (optional)' : 'First time'} error={fieldError(save.error, 'dueOn')}>
              {(id) => <Input id={id} name="dueOn" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}
            </Field>
          ) : null}
          <Field label="Minutes (optional)">{(id) => <Input id={id} name="estimatedMinutes" type="number" min={1} max={10080} defaultValue={task?.estimatedMinutes ?? ''} />}</Field>
          {task?.parent ? (
            <p className="text-sm text-[var(--muted)]">Points: {task.points ?? 0}, this subtask's share of “{task.parent.title}”. Change the points on the main task.</p>
          ) : (
            <Field label="Points for kids (optional)">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={task?.points ?? ''} />}</Field>
          )}
        </div>
        {canRepeat ? (
          <Field label="Repeat" hint={repeat !== 'NEVER' && repeat !== 'CUSTOM' ? summaryOf(presetSchedule(repeat, dueOn || today())) : undefined}>
            {(id) => (
              <Select id={id} value={repeat} onChange={(e) => setRepeat(e.target.value as Repeat)}>
                <option value="NEVER">Does not repeat</option>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="YEARLY">Yearly</option>
                <option value="CUSTOM">Custom…</option>
              </Select>
            )}
          </Field>
        ) : null}
        {canRepeat && repeat === 'CUSTOM' ? <ScheduleFields startsOn={dueOn || undefined} /> : null}
        {!task ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={claim} onChange={(e) => setClaim(e.target.checked)} /> I'll do this myself (otherwise it goes to the House Pool)
          </label>
        ) : null}
        {save.error && !(save.error instanceof ApiError && save.error.code === 'VALIDATION_FAILED') ? <ErrorText error={save.error} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>{task ? 'Save' : 'Add task'}</Button>
        </div>
      </form>
    </Modal>
  );
}
